import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod/v4';

/**
 * `resume` marks an arrival from a "Continue setup" entry point on a DRAFT vehicle (SWOOP-231): the
 * layout resolves the first incomplete step from the persisted record and redirects there once.
 *
 * It lives in the URL rather than in router state so the redirect survives a reload and stays a single
 * resolution site for both entry points (the profile banner and the vehicles-list row).
 *
 * Only a literal `true` opts in — accepted as both the boolean (the router's own parser JSON-decodes
 * `?resume=true`) and the raw string (a hand-typed or re-encoded URL). Deliberately NOT
 * `z.coerce.boolean()`, which routes every truthy value to `true`: that would make `?resume=false`
 * resume, since `Boolean('false')` is true. Anything else — `false`, junk, a nested object — degrades
 * to `undefined` via `.catch()` rather than throwing, so a malformed flag can never blank the wizard.
 */
const wizardSearchSchema = z.object({
  resume: z
    .union([z.literal(true), z.literal('true')])
    .transform((): true => true)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_authenticated/vehicles_/$vehicleId')({
  beforeLoad: ({ context }): void => {
    if (context.permissions.isReady && !context.permissions.hasPermission('vehicles:manage')) {
      throw redirect({ to: '/vehicles' });
    }
  },
  component: lazyRouteComponent(() => import('&/features/vehicles/pages/vehicle-wizard-layout-page')),
  staticData: { dynamic: true },
  validateSearch: (search) => wizardSearchSchema.parse(search),
});
